import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query'
import {api} from '@/lib/api'
import type {UploadTarget} from '@/hooks/sgsst/evidencias/use-evidence'
import type {Page} from '@/hooks/empresa/organizacion/use-businesses'
import type {components} from '@/lib/api-schema'
import {mintLicenciaUploadTarget, useLicenciaMutations, type LicenciaCategoria} from '@/hooks/inci/consola/use-licencia'
import {createRosterHooks} from '@/hooks/common/use-roster'

export type Partner = components['schemas']['PartnerRead']
export type PartnerCreateResult = components['schemas']['PartnerCreateRead']
export type PartnerLicencia = components['schemas']['PersonaLicenciaRead']
export type PartnerTipo = components['schemas']['PartnerTipo']
export type PartnerMember = components['schemas']['PartnerMemberRead']
export type PartnerMemberCreateResult = components['schemas']['PartnerMemberCreateRead']
export type PartnerBillingCycle = components['schemas']['PartnerBillingCycleRead']['billing_cycle_preferido']

export const PARTNERS_PAGE_SIZE = 20
const PARTNER_MEMBERS_PAGE_SIZE = 20

const roster = createRosterHooks<Partner>('/admin/partners', 'partners')

export function usePartners(params: {page: number; search: string}, enabled = true) {
  return roster.useList(params, PARTNERS_PAGE_SIZE, enabled)
}

export const useDeactivatePartner = roster.useDeactivate
export const useReactivatePartner = roster.useReactivate

export function usePartner(userId: string | undefined) {
  return useQuery({
    queryKey: ['partners', userId],
    queryFn: async () => (await api.get<Partner>(`/admin/partners/${userId}`)).data,
    enabled: !!userId,
  })
}

export function useCreatePartner() {
  const invalidate = roster.useInvalidate()
  return useMutation({
    mutationFn: async (input: {
      cedula: string
      name: string
      last_name: string
      email: string
      phone?: string
      quotas: Record<string, number>
      tipo: PartnerTipo
      nit?: string | null
      razon_social?: string | null
    }) => (await api.post<PartnerCreateResult>('/admin/partners', input)).data,
    onSuccess: invalidate,
  })
}

/** Va por `partner_id` (el de la organización), no por `user_id` como el resto del router. */
export function usePartnerMembers(partnerId: string | undefined, params: {page: number; search: string}) {
  return useQuery({
    queryKey: ['partners', partnerId, 'members', params],
    queryFn: async () =>
      (
        await api.get<Page<PartnerMember>>(`/admin/partners/${partnerId}/members`, {
          params: {page: params.page, page_size: PARTNER_MEMBERS_PAGE_SIZE, search: params.search || undefined},
        })
      ).data,
    enabled: !!partnerId,
    placeholderData: (previous) => previous,
  })
}

export function useCreatePartnerMember(partnerId: string | undefined) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: {cedula: string; name: string; last_name: string; email: string; phone?: string}) =>
      (await api.post<PartnerMemberCreateResult>(`/admin/partners/${partnerId}/members`, input)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['partners', partnerId, 'members']})
      queryClient.invalidateQueries({queryKey: ['partners']})
    },
  })
}

export function useRemovePartnerMember(partnerId: string | undefined) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (memberId: string) => api.delete(`/admin/partners/${partnerId}/members/${memberId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['partners', partnerId, 'members']})
      queryClient.invalidateQueries({queryKey: ['partners']})
    },
  })
}

export function useUpdatePartnerQuota() {
  const invalidate = roster.useInvalidate()
  return useMutation({
    mutationFn: async ({userId, quotas}: {userId: string; quotas: Record<string, number>}) =>
      (await api.patch<Partner>(`/admin/partners/${userId}`, {quotas})).data,
    onSuccess: invalidate,
  })
}

export function useUpdatePartnerBillingCycleAdmin(userId: string | undefined) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (billingCycle: PartnerBillingCycle) =>
      (
        await api.patch<components['schemas']['PartnerBillingCycleRead']>(`/admin/partners/${userId}/billing-cycle`, {
          billing_cycle_preferido: billingCycle,
        })
      ).data,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['partners', userId]})
      queryClient.invalidateQueries({queryKey: ['partners']})
    },
  })
}

export async function mintPartnerLicenciaUploadTarget(userId: string, file: File): Promise<UploadTarget> {
  return mintLicenciaUploadTarget(`/admin/partners/${userId}/licencia`, file)
}

type PartnerLicenciaSaveInput = {
  userId: string
  numero: string
  categoria: LicenciaCategoria
  vigencia: string
  doc_key: string
}

export function usePartnerLicenciaMutations() {
  const invalidate = roster.useInvalidate()
  return useLicenciaMutations<PartnerLicencia, PartnerLicenciaSaveInput>(
    (userId) => `/admin/partners/${userId}/licencia`,
    invalidate
  )
}
